import once from "lodash/once";
import {
  DEV_SSO_URL,
  TEST_SSO_URL,
  PROD_SSO_URL,
  MIN_PORT_FOR_PROXYING_TO_TEST_API,
  MAX_PORT_FOR_PROXYING_TO_TEST_API,
  MIN_PORT_FOR_PROXYING_TO_PROD_API,
  MAX_PORT_FOR_PROXYING_TO_PROD_API,
} from "../constants/urls";
import { makeGetEnvironmentFromUrlDeprecationWarn } from "../helpers/warnings";

export const runtimeEnvNames = [
  "local",
  "development",
  "test",
  "production",
] as const;

export type RuntimeEnvName = typeof runtimeEnvNames[number];
export type GranularRuntimeEnvName =
  | "granappdevelopment"
  | "granapptest"
  | "granappproduction";

const runtimeEnvToGranularRuntimeEnv: Record<
  RuntimeEnvName,
  GranularRuntimeEnvName
> = {
  local: "granappdevelopment",
  development: "granappdevelopment",
  test: "granapptest",
  production: "granappproduction",
};

const warnWhenRuntimeEnvNameNotFound = once(() => {
  console.warn(
    "[@granular/fabric-core] RUNTIME_ENV_NAME could not be found. We will assume this is a local development environment.",
  );
});
const warnWhenRuntimeEnvNameNotValid = once((value: string) => {
  console.warn(
    `[@granular/fabric-core] RUNTIME_ENV_NAME '${value}' is not valid. We will assume this is a production environment.`,
  );
});

export const getRuntimeEnvName = (): RuntimeEnvName => {
  // This value is set by "detectRuntimeEnvName" in
  // https://gitlab.internal.granular.ag/fabric/container/-/blob/master/public/index.html
  const runtimeEnvName = (window as any).RUNTIME_ENV_NAME;
  if (process.env.NODE_ENV === "test") {
    return "local";
  }
  if (!runtimeEnvName) {
    warnWhenRuntimeEnvNameNotFound();
    return "local";
  }
  if (!runtimeEnvNames.includes(runtimeEnvName)) {
    warnWhenRuntimeEnvNameNotValid(runtimeEnvName);
    return "production";
  }
  return runtimeEnvName;
};

export const getGranularRuntimeEnvName = (): GranularRuntimeEnvName => {
  return runtimeEnvToGranularRuntimeEnv[getRuntimeEnvName()];
};

const deprecationWarn = makeGetEnvironmentFromUrlDeprecationWarn();

/**
 * @deprecated Use getGranularRuntimeEnvName instead.
 */
export const getEnvironmentFromUrl = (): GranularRuntimeEnvName => {
  deprecationWarn();
  return getGranularRuntimeEnvName();
};

/**
 * @deprecated Use getRegexMatchingFromUrl instead.
 */
export const regexForGetOperationFromUrl = /\/operations\/([^\/]*)/i;

/*
  This function will return a Regex that will match with any translated string that is sent for the route param.
  Multiple languages can be returned for routing.

  The route param should come from the translation files.
*/
export const getRegexMatchingFromUrl = (route: string) =>
  new RegExp(`/${route}/([^\\/]*)`, "i");

export const getIsRunningOnPortForProxyingApiToTest = (
  port: number,
): boolean => {
  return (
    port >= MIN_PORT_FOR_PROXYING_TO_TEST_API &&
    port <= MAX_PORT_FOR_PROXYING_TO_TEST_API
  );
};

export const getIsRunningOnPortForProxyingApiToProd = (
  port: number,
): boolean => {
  return (
    port >= MIN_PORT_FOR_PROXYING_TO_PROD_API &&
    port <= MAX_PORT_FOR_PROXYING_TO_PROD_API
  );
};

export const getLocalSsoOverrideUrl = (): string | undefined => {
  if (
    window.location.hostname === "localhost" &&
    getIsRunningOnPortForProxyingApiToTest(Number(window.location.port))
  ) {
    return TEST_SSO_URL;
  }
  if (
    window.location.hostname === "localhost" &&
    getIsRunningOnPortForProxyingApiToProd(Number(window.location.port))
  ) {
    return PROD_SSO_URL;
  }

  return undefined;
};

export const getSSOUrl = once((): string => {
  const localSsoOverrideUrl = getLocalSsoOverrideUrl();
  if (localSsoOverrideUrl) {
    console.debug(
      `[@granular/fabric-core][getSSOUrl] Using SSO at "${localSsoOverrideUrl}" due to the localhost port`,
    );
    return localSsoOverrideUrl;
  }

  const env = getGranularRuntimeEnvName();
  if (env === "granappdevelopment") {
    console.debug(
      `[@granular/fabric-core][getSSOUrl] Using SSO at ${DEV_SSO_URL} due to the URL`,
    );
    return DEV_SSO_URL;
  }
  if (env === "granapptest") {
    console.debug(
      `[@granular/fabric-core][getSSOUrl] Using SSO at ${TEST_SSO_URL} due to the URL`,
    );
    return TEST_SSO_URL;
  }

  console.debug(
    `[@granular/fabric-core][getSSOUrl] Using SSO at ${PROD_SSO_URL} as this is the default`,
  );
  return PROD_SSO_URL;
});
